import { Divider } from '@material-ui/core'
import InfoOutlined from '@material-ui/icons/InfoOutlined'
import { Alert, ComponentVersions_3_0, ComponentVersionsInherit, styled, Tooltip, Typography } from 'opensolar-ui'
import { useMemo } from 'react'
import { useTranslate } from 'react-admin'
import { useField } from 'react-final-form'
import { makeOpenSolarStyles } from 'themes/makeOpenSolarStyles'
import { MountingID, MountingSystemType, MountingSystemTypeOptions } from 'types/mounting'
import { useFeatureFlag } from 'util/featureFlags'
import { RemoveComponentButton } from '../../../../../../../elements/componentSelector/RemoveComponentButton'
import { getIncompatibleMountingSystems, getNativeMountingSystems } from '../nativeMounting/mountingSystems'
import {
  filterIncompatibleMountingIdsByRoofType,
  filterMountingIdsByRoofType,
  getMountingCompatibleRoofTypes,
  isRoofTypeRequiredForMountingSelection,
} from '../nativeMounting/roofTypeCompatibility'
import { RoofTypeName } from '../nativeMounting/types'
import { getRoofName } from '../nativeMounting/utils'
import { useGetMountingOptsInCountry } from './MountingSelector'
import MountingSystemCapsule, { mountingSystems } from './MountingSystemCapsule'

const useSelectedMountingSystemStyles = makeOpenSolarStyles((theme) => ({
  divider: {
    margin: '10px 0',
  },
  selectedMountingSystem: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}))

const SelectedMountingSystem = ({
  selectedMountingSystem,
  clearMountingSystem,
}: {
  selectedMountingSystem: MountingID
  clearMountingSystem(): void
}) => {
  const classes = useSelectedMountingSystemStyles()
  const translate = useTranslate()
  return (
    <>
      <Divider className={classes.divider} />
      <Typography variant="subtitle2" gutterBottom>
        {translate('Selected Mounting System')}
      </Typography>
      <div className={classes.selectedMountingSystem}>
        <MountingSystemCapsule integrationName={selectedMountingSystem} />
        <RemoveComponentButton onClick={clearMountingSystem} data-testid="remove-mounting-system" />
      </div>
    </>
  )
}

export const useFilteredMountingIds = (mountingIds: MountingID[], mountingSystemType?: MountingSystemType) => {
  const fastensolEnabled = useFeatureFlag('mounting_fastensol', 'on')
  const gseEnabled = useFeatureFlag('mounting_gseinroof', 'on')
  const sunmodoEnabled = useFeatureFlag('sunmodo_beta', 'on')
  const k2Enabled = useFeatureFlag('k2_usa', 'on')
  const k2UkEnabled = useFeatureFlag('mounting_k2uk', 'on')
  const uniracEnabled = useFeatureFlag('unirac_usa', 'on')
  const solarNestGroundMountEnabled = useFeatureFlag('mounting_solarnest_groundmount', 'on')
  const solarNestRooftopEnabled = useFeatureFlag('mounting_solarnest_rooftop', 'on')
  const msPitchedProLineEnabled = useFeatureFlag('mounting_mspitchedproline', 'on')
  const msTrapezeProLineEnabled = useFeatureFlag('mounting_mstrapezeproline', 'on')
  const variosoleEnabled = useFeatureFlag('mounting_variosole', 'on')
  const solarRoofProEnabled = useFeatureFlag('mounting_solarroofpro', 'on')
  const ironridgeEnabled = useFeatureFlag('mounting_ironridge', 'on')
  const metasoleEnabled = useFeatureFlag('mounting_metasole', 'on')
  const schletterEnabled = useFeatureFlag('mounting_schletter', 'on')
  const renusolConfiguratorEnabled = useFeatureFlag('mounting_renusol_configurator', 'on')
  const esdecEnabled = useFeatureFlag('mounting_esdec_clickfit_evo', 'on')
  const viridianEnabled = useFeatureFlag('mounting_viridian', 'on')
  const flatFixEnabled = useFeatureFlag('mounting_esdec_flatfix_fusion', 'on')
  const solarPortGroundMountEnabled = useFeatureFlag('mounting_solarport_groundmount', 'on')
  const novaEnabled = useFeatureFlag('mounting_nova', 'on')
  const vanDerValkFlatRoofEnabled = useFeatureFlag('mounting_vandervalk_flat_roof', 'on')
  const vanDerValkPitchedRoofEnabled = useFeatureFlag('mounting_vandervalk_pitched_roof', 'on')
  const fastRackEnabled = useFeatureFlag('mounting_fastrack', 'on')
  const snapnrackUltraRailEnabled = useFeatureFlag('mounting_snapnrack_ultrarail', 'on')

  const filteredMountingIds = mountingIds.filter((id) => {
    if (id === 'vanDerValk') {
      if (mountingSystemType === 'onRoof') {
        return vanDerValkPitchedRoofEnabled
      }
      if (mountingSystemType === 'flatRoof') {
        return vanDerValkFlatRoofEnabled
      }
      return vanDerValkFlatRoofEnabled || vanDerValkPitchedRoofEnabled
    }

    if (
      (!fastensolEnabled && id === 'fastensol') ||
      (!gseEnabled && id === 'gseInRoof') ||
      (!sunmodoEnabled && (id === 'sunmodoSMRPitched' || id === 'sunmodoNanoRack')) ||
      (!k2Enabled && ['k2OnRoof', 'k2SimpleTilt', 'k2GroundMount'].includes(id)) ||
      (!k2UkEnabled && id === 'k2OnRoofUK') ||
      (!uniracEnabled && id === 'unirac') ||
      (!solarNestGroundMountEnabled && id === 'solarNestGroundMount') ||
      (!solarNestRooftopEnabled && id === 'solarNestRooftop') ||
      (!msPitchedProLineEnabled && id === 'msPitchedProLine') ||
      (!msTrapezeProLineEnabled && id === 'msTrapezeProLine') ||
      (!variosoleEnabled && id === 'variosole') ||
      (!solarRoofProEnabled && id === 'solarRoofPro') ||
      (!ironridgeEnabled && id === 'ironridge') ||
      (!metasoleEnabled && id === 'metasole') ||
      (!schletterEnabled && id === 'schletter') ||
      (!renusolConfiguratorEnabled && id === 'renusol') ||
      (!esdecEnabled && id === 'esdec') ||
      (!viridianEnabled && id === 'viridian') ||
      (!flatFixEnabled && id === 'flatFix') ||
      (!solarPortGroundMountEnabled && id === 'solarPortGroundMount') ||
      (!novaEnabled && id === 'nova') ||
      (!fastRackEnabled && id === 'fastRack') ||
      (!snapnrackUltraRailEnabled && id === 'snapnrackUltraRail')
    ) {
      return false
    } else {
      return true
    }
  })

  return filteredMountingIds
}

const MULTIPLE_PANELS_TOOLTIP =
  'Whoops. This mounting system is not compatible with multiple panel types on OpenSolar. Select different mounting, or remove multiple panel types from system to use this mounting.'
const ROOF_TYPE_INCOMPATIBLE_TOOLTIP = 'The following mounting systems are incompatible with the selected roof type'

const StyledEmptyStateAlert = styled(Alert)({
  marginTop: '10px',
  marginBottom: '8px',
})

const useMountingSystemSelectorStyles = makeOpenSolarStyles(() => ({
  divider: {
    margin: '10px 0',
  },
}))

const useIncompatibleMountingSystemsStyles = makeOpenSolarStyles((theme) => ({
  incompatibleSection: {},
  incompatibleViewAllSection: {
    paddingTop: 10,
  },
  incompatibleHeader: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    cursor: 'help',
  },
  incompatibleTooltipContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: 4,
    maxWidth: 320,
    maxHeight: 360,
    overflowY: 'auto',
  },
  incompatibleSystemRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
  },
  incompatibleSystemImageWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    minWidth: 48,
    height: 32,
  },
  incompatibleSystemImage: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
  incompatibleSystemDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 0,
  },
  systemName: {
    fontWeight: 600,
  },
  compatibleWithText: {
    color: theme.palette.text.secondary,
    fontStyle: 'normal',
  },
  infoIcon: {
    color: theme.palette.text.secondary,
    fontSize: 18,
  },
}))

const IncompatibleMountingSystemTooltipRow = ({
  mountingId,
  nativeSystems,
}: {
  mountingId: MountingID
  nativeSystems: ReturnType<typeof getNativeMountingSystems>
}) => {
  const translate = useTranslate()
  const classes = useIncompatibleMountingSystemsStyles()
  const displayConfig = mountingSystems.find((config) => config.id === mountingId)
  const compatibleRoofTypes = getMountingCompatibleRoofTypes(mountingId, nativeSystems)

  if (!displayConfig) {
    return null
  }

  const compatibleRoofTypesLabel = compatibleRoofTypes
    ? compatibleRoofTypes.map((roofType) => translate(roofType, { _: roofType })).join(', ')
    : translate('All roof types')

  return (
    <div className={classes.incompatibleSystemRow}>
      <div className={classes.incompatibleSystemImageWrapper}>
        <img
          src={`${window.PUBLIC_URL}/images/${displayConfig.imageFile}`}
          alt={displayConfig.displayName}
          className={classes.incompatibleSystemImage}
        />
      </div>
      <div className={classes.incompatibleSystemDetails}>
        <Typography variant="body2" className={classes.systemName}>
          {displayConfig.displayName}
        </Typography>
        <Typography variant="caption" className={classes.compatibleWithText}>
          {translate('Compatible with: %{roofTypes}', { roofTypes: compatibleRoofTypesLabel })}
        </Typography>
      </div>
    </div>
  )
}

const IncompatibleMountingSystemsTooltipContent = ({ mountingIds }: { mountingIds: MountingID[] }) => {
  const translate = useTranslate()
  const classes = useIncompatibleMountingSystemsStyles()
  const nativeSystems = getNativeMountingSystems()

  return (
    <div className={classes.incompatibleTooltipContent}>
      <Typography variant="body2">{translate(ROOF_TYPE_INCOMPATIBLE_TOOLTIP)}</Typography>
      {mountingIds.map((mountingId) => (
        <IncompatibleMountingSystemTooltipRow key={mountingId} mountingId={mountingId} nativeSystems={nativeSystems} />
      ))}
    </div>
  )
}

const IncompatibleMountingSystemsSection = ({
  mountingIds,
  showNoCompatibleMessage,
}: {
  mountingIds: MountingID[]
  showNoCompatibleMessage: boolean
}) => {
  const translate = useTranslate()
  const classes = useIncompatibleMountingSystemsStyles()

  return (
    <div className={classes.incompatibleSection}>
      {showNoCompatibleMessage && (
        <ComponentVersionsInherit versions={ComponentVersions_3_0}>
          <StyledEmptyStateAlert severity="neutral">
            <div>{translate('No mounting systems are compatible with this roof type')}</div>
          </StyledEmptyStateAlert>
        </ComponentVersionsInherit>
      )}
      <div className={classes.incompatibleViewAllSection}>
        <Tooltip
          title={<IncompatibleMountingSystemsTooltipContent mountingIds={mountingIds} />}
          arrow
          placement="right"
        >
          <div className={classes.incompatibleHeader}>
            <Typography variant="body2">{translate('View all mounting systems')}</Typography>
            <InfoOutlined className={classes.infoIcon} />
          </div>
        </Tooltip>
      </div>
    </div>
  )
}

const SelectableMountingSystems = ({
  selectedTypeOption,
  handleUpdateMountingSystem,
  roofTypeName,
}: {
  selectedTypeOption: MountingSystemTypeOptions
  handleUpdateMountingSystem(mountingId: MountingID): void
  roofTypeName: RoofTypeName | null
}) => {
  const translate = useTranslate()
  const classes = useMountingSystemSelectorStyles()

  const incompatibleMountingSystems = getIncompatibleMountingSystems()
  const nativeSystems = getNativeMountingSystems()

  const mountingIds = selectedTypeOption?.mountingIds
  const filteredMountingIds = useFilteredMountingIds(mountingIds, selectedTypeOption.type)
  const compatibleMountingIds = filterMountingIdsByRoofType(
    filteredMountingIds,
    roofTypeName,
    nativeSystems,
    selectedTypeOption.type
  )
  const incompatibleByRoofTypeIds = filterIncompatibleMountingIdsByRoofType(
    filteredMountingIds,
    roofTypeName,
    nativeSystems,
    selectedTypeOption.type
  )

  if (filteredMountingIds.length === 0) {
    return null
  }

  if (compatibleMountingIds.length === 0 && incompatibleByRoofTypeIds.length === 0) {
    return null
  }

  return (
    <>
      {compatibleMountingIds.length > 0 && (
        <>
          <Divider className={classes.divider} />
          <Typography variant="subtitle2" gutterBottom>
            {translate('Select Mounting System')}
          </Typography>
          {compatibleMountingIds.map((mountingId: MountingID) => {
            const isDisabledByMultiplePanels = incompatibleMountingSystems.includes(mountingId)
            return (
              <Tooltip
                key={mountingId}
                color="primary"
                title={isDisabledByMultiplePanels ? translate(MULTIPLE_PANELS_TOOLTIP) : ''}
                arrow
                placement="right"
              >
                <div>
                  <MountingSystemCapsule
                    integrationName={mountingId}
                    isDisabled={isDisabledByMultiplePanels}
                    onClick={() => handleUpdateMountingSystem(mountingId)}
                  />
                </div>
              </Tooltip>
            )
          })}
        </>
      )}
      {incompatibleByRoofTypeIds.length > 0 && (
        <IncompatibleMountingSystemsSection
          mountingIds={incompatibleByRoofTypeIds}
          showNoCompatibleMessage={compatibleMountingIds.length === 0}
        />
      )}
    </>
  )
}

const MountingSystemSelector = ({
  selectedType,
  selectedMountingSystem,
  handleUpdateMountingSystem,
  isRoofTypeSelectorDisabled,
}: {
  selectedType: MountingSystemType
  selectedMountingSystem: MountingID | undefined
  handleUpdateMountingSystem(mountingId: MountingID | undefined): void
  isRoofTypeSelectorDisabled: boolean
}) => {
  const clearMountingSystem = () => {
    handleUpdateMountingSystem(undefined)
  }

  const {
    input: { value: roofTypeValue },
  } = useField('roof_type', { subscription: { value: true } })
  const roofTypeName = useMemo(() => getRoofName(roofTypeValue) ?? null, [roofTypeValue])

  const mountingTypeOptions = useGetMountingOptsInCountry()

  const selectedTypeOption = useMemo((): MountingSystemTypeOptions | null => {
    return mountingTypeOptions.find((option) => option?.type === selectedType) || null
  }, [selectedType])

  if (selectedMountingSystem !== undefined) {
    return (
      <SelectedMountingSystem
        selectedMountingSystem={selectedMountingSystem}
        clearMountingSystem={clearMountingSystem}
      />
    )
  }

  const requiresRoofTypeSelection = isRoofTypeRequiredForMountingSelection(selectedType, isRoofTypeSelectorDisabled)

  if (requiresRoofTypeSelection && roofTypeName === null) {
    return null
  }

  if (selectedTypeOption) {
    return (
      <SelectableMountingSystems
        selectedTypeOption={selectedTypeOption}
        handleUpdateMountingSystem={handleUpdateMountingSystem}
        roofTypeName={roofTypeName}
      />
    )
  }
  return null
}

export default MountingSystemSelector
